import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Check, ChevronLeft, ChevronRight, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchServices, fetchStaff, createBooking, fetchAvailability } from "../lib/api";
import { format } from "date-fns";
import { useAuth } from "../context/AuthContext";

const TIME_SLOTS = ["09:30", "10:30", "11:30", "13:00", "14:00", "15:00", "16:00", "17:00"];

export const Book = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const preselectedServiceId = params.get("service");
  const { user, loading: authLoading, signIn } = useAuth();

  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [bookedTimes, setBookedTimes] = useState([]);

  // Fetch booked slots whenever the selected date changes
  useEffect(() => {
    if (!date) { setBookedTimes([]); return; }
    const d = format(date, "yyyy-MM-dd");
    fetchAvailability(d)
      .then((res) => setBookedTimes(res.booked_times || []))
      .catch(() => setBookedTimes([]));
    // Clear a previously chosen time if it just became booked
    setTime((t) => t);
  }, [date]);

  // Prefill from signed-in user
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    fetchServices().then((s) => {
      setServices(s);
      if (preselectedServiceId) {
        const found = s.find((x) => x.id === preselectedServiceId);
        if (found) { setSelectedService(found); setStep(2); }
      }
    });
    fetchStaff().then(setStaff);
  }, [preselectedServiceId]);

  const grouped = useMemo(() => {
    const g = {};
    services.forEach((s) => {
      if (!g[s.category]) g[s.category] = [];
      g[s.category].push(s);
    });
    return g;
  }, [services]);

  const canProceed = () => {
    if (step === 1) return !!selectedService;
    if (step === 2) return !!date;
    if (step === 3) return !!time;
    if (step === 4) return form.name && form.email && form.phone;
    return false;
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        service_id: selectedService.id,
        staff_id: staff[0]?.id || null,
        date: format(date, "yyyy-MM-dd"),
        time,
        notes: form.notes,
      };
      const booking = await createBooking(payload);
      setConfirmedBooking(booking);
      toast.success("Booking confirmed! Check your inbox ♡");
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Please sign in to book.");
        signIn();
        return;
      }
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Auth gate — must sign in before booking
  if (!authLoading && !user) {
    return (
      <div className="pt-20 px-6 text-center" data-testid="book-signin-gate">
        <div className="w-20 h-20 rounded-full bg-blush/40 mx-auto flex items-center justify-center shadow-glow">
          <Sparkles size={32} className="text-espresso" strokeWidth={1.5} />
        </div>
        <p className="script text-3xl text-gold mt-6">reserve your moment</p>
        <h1 className="text-4xl font-serif text-espresso mt-1 leading-tight">Sign in to book</h1>
        <p className="text-mocha mt-3 max-w-xs mx-auto text-sm leading-relaxed">
          A quick sign-in with Google lets us confirm your appointment and remember your details next time.
        </p>
        <button
          onClick={signIn}
          data-testid="signin-google-btn"
          className="mt-8 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white border border-borderSoft shadow-soft hover:shadow-glow transition text-espresso text-sm"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Sign in with Google
        </button>
        <p className="text-mocha text-xs mt-6">We only use your name & email to confirm the booking.</p>
      </div>
    );
  }

  if (confirmedBooking) {
    return (
      <div className="pt-16 px-6 text-center" data-testid="booking-confirmed">
        <div className="w-20 h-20 rounded-full bg-blush/40 mx-auto flex items-center justify-center shadow-glow">
          <Check size={40} className="text-espresso" strokeWidth={1.5} />
        </div>
        <p className="script text-3xl text-gold mt-6">merci beaucoup</p>
        <h1 className="text-4xl font-serif text-espresso mt-1">You're booked!</h1>
        <p className="text-mocha mt-3 max-w-xs mx-auto">A confirmation is on its way to <strong>{confirmedBooking.customer_email}</strong>.</p>

        <div className="mt-8 bg-white rounded-[2rem] p-6 text-left shadow-soft">
          <p className="text-xs uppercase tracking-widest text-gold">Your appointment</p>
          <h3 className="text-2xl font-serif text-espresso mt-1">{confirmedBooking.service_name}</h3>
          <div className="mt-4 space-y-2 text-sm text-espresso">
            <div className="flex justify-between"><span className="text-mocha">Date</span><span>{confirmedBooking.date}</span></div>
            <div className="flex justify-between"><span className="text-mocha">Time</span><span>{confirmedBooking.time}</span></div>
            <div className="flex justify-between"><span className="text-mocha">With</span><span>{confirmedBooking.staff_name || "Any available artist"}</span></div>
            <div className="flex justify-between"><span className="text-mocha">Total</span><span className="text-gold font-semibold">${confirmedBooking.service_price.toFixed(2)}</span></div>
          </div>
        </div>

        <button
          onClick={() => navigate("/")}
          data-testid="back-home-btn"
          className="mt-8 inline-block px-8 py-3 rounded-full bg-espresso text-cream text-sm hover:shadow-glow transition"
        >
          Back to Home
        </button>
      </div>
    );
  }

  const StepIndicator = () => (
    <div className="flex items-center gap-1.5 mb-6" data-testid="step-indicator">
      {[1, 2, 3, 4].map((n) => (
        <div
          key={n}
          className={`h-1 flex-1 rounded-full transition-all ${
            n <= step ? "bg-espresso" : "bg-borderSoft"
          }`}
        />
      ))}
    </div>
  );

  return (
    <div className="pt-10 px-6 pb-8" data-testid="book-page">
      <div className="flex items-center justify-between mb-2">
        <p className="script text-2xl text-gold">reserve your moment</p>
        <span className="text-xs text-mocha">Step {step} of 4</span>
      </div>
      <h1 className="text-4xl font-serif text-espresso mb-6 leading-tight">
        {step === 1 && "Choose a service"}
        {step === 2 && "Pick a date"}
        {step === 3 && "Pick a time"}
        {step === 4 && "Your details"}
      </h1>
      <StepIndicator />

      {/* Step 1: Service */}
      {step === 1 && (
        <div className="space-y-6" data-testid="step-service">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <p className="text-xs uppercase tracking-widest text-gold mb-2">{cat}</p>
              <div className="space-y-2">
                {items.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedService(s)}
                    data-testid={`select-service-${s.id}`}
                    className={`w-full text-left p-4 rounded-2xl border transition-all ${
                      selectedService?.id === s.id
                        ? "border-blushDeep bg-blush/20 shadow-glow"
                        : "border-borderSoft bg-white hover:border-blush"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-serif text-espresso text-lg leading-tight">{s.name}</h3>
                        <p className="text-mocha text-xs mt-1">{s.duration} min</p>
                      </div>
                      <p className="text-espresso font-serif text-xl">${s.price}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step 2: Date */}
      {step === 2 && (
        <div className="bg-white rounded-[2rem] p-4 shadow-soft flex justify-center" data-testid="step-date">
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || d.getDay() === 0}
            className="rounded-2xl"
          />
        </div>
      )}

      {/* Step 3: Time */}
      {step === 3 && (
        <div data-testid="step-time">
          <p className="text-mocha text-sm mb-4">
            {date && `Available slots for ${format(date, "EEEE, MMMM d")}`}
          </p>
          <div className="grid grid-cols-3 gap-3">
            {TIME_SLOTS.map((t) => {
              const isBooked = bookedTimes.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => !isBooked && setTime(t)}
                  disabled={isBooked}
                  data-testid={`time-${t}`}
                  className={`py-3 rounded-full border text-sm transition-all ${
                    isBooked
                      ? "bg-creamDeep text-mocha/50 border-borderSoft line-through cursor-not-allowed"
                      : time === t
                      ? "bg-espresso text-cream border-espresso shadow-soft"
                      : "bg-white text-espresso border-borderSoft hover:border-blush"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
          {bookedTimes.length > 0 && (
            <p className="text-mocha text-xs mt-3" data-testid="booked-note">
              Struck-through times are already booked.
            </p>
          )}
        </div>
      )}

      {/* Step 4: Details */}
      {step === 4 && (
        <div className="space-y-4" data-testid="step-details">
          <div>
            <Label htmlFor="name" className="text-mocha text-xs uppercase tracking-widest">Name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 rounded-2xl border-borderSoft bg-white h-12 px-4"
              data-testid="input-name"
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-mocha text-xs uppercase tracking-widest">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 rounded-2xl border-borderSoft bg-white h-12 px-4"
              data-testid="input-email"
            />
          </div>
          <div>
            <Label htmlFor="phone" className="text-mocha text-xs uppercase tracking-widest">Phone</Label>
            <Input
              id="phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1 rounded-2xl border-borderSoft bg-white h-12 px-4"
              data-testid="input-phone"
            />
          </div>
          <div>
            <Label htmlFor="notes" className="text-mocha text-xs uppercase tracking-widest">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 rounded-2xl border-borderSoft bg-white"
              data-testid="input-notes"
              placeholder="Anything we should know?"
            />
          </div>

          <div className="mt-6 p-4 bg-creamDeep rounded-2xl text-sm">
            <p className="text-xs uppercase tracking-widest text-gold">Summary</p>
            <p className="text-espresso font-serif text-lg mt-1">{selectedService?.name}</p>
            <p className="text-mocha">{date && format(date, "MMM d, yyyy")} · {time}</p>
            <p className="text-mocha">with {staff[0]?.name || "Simmi Chahal"}</p>
            <p className="text-gold mt-2">${selectedService?.price?.toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Nav */}
      <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-[92%] max-w-md flex gap-3 z-40">
        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            data-testid="back-btn"
            className="w-14 h-14 rounded-full bg-white border border-borderSoft shadow-soft flex items-center justify-center text-espresso hover:bg-creamDeep transition"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        {step < 4 ? (
          <button
            onClick={() => canProceed() && setStep(step + 1)}
            disabled={!canProceed()}
            data-testid="next-btn"
            className={`flex-1 h-14 rounded-full text-sm font-medium flex items-center justify-center gap-2 transition-all ${
              canProceed()
                ? "bg-espresso text-cream shadow-glow hover:shadow-gold"
                : "bg-borderSoft text-mocha cursor-not-allowed"
            }`}
          >
            Continue <ChevronRight size={18} />
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={!canProceed() || loading}
            data-testid="confirm-booking-btn"
            className={`flex-1 h-14 rounded-full text-sm font-medium flex items-center justify-center gap-2 transition-all ${
              canProceed() && !loading
                ? "bg-espresso text-cream shadow-glow hover:shadow-gold"
                : "bg-borderSoft text-mocha cursor-not-allowed"
            }`}
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <><Sparkles size={16} /> Confirm booking</>}
          </button>
        )}
      </div>
    </div>
  );
};

export default Book;
